import { gql } from 'graphql-request';

export const BaseShopFragment = gql`
  fragment BaseShop on Shop {
    id
    originalPrice: price
    currency
    inventory {
      orderable
      stock_level
    }
    sku
    step_quantity
    price: calculatedPrice
    productUrl
    ctaButtonType
    mainCategory
    mainImageUrl
    purchasing {
      hideCTA
      traegerExclusive
      vipOnly
    }
    discount
    isGrill
    isExtendedWarranty
    description
    weight
    color
    title
    upc
    variationValues {
      name
      value
    }
    variations {
      customFields
      id
      price: calculatedPrice
      inventory {
        orderable
      }
      variationValues {
        name
        value
      }
    }
  }
`;

export const ShopFragment = gql`
  ${BaseShopFragment}
  fragment Shop on Shop {
    ...BaseShop
    customFields
    disableLocally
    variationAttributes {
      id
      name
      values {
        value
      }
    }
    variationValues {
      name
      value
    }
    variations {
      colorVariationImage
      customFields
      title
      productUrl
      mainImageUrl
      mainCategory
      description
      ctaButtonType
      originalPrice: price
      price: calculatedPrice
      discount
      id
      inventory {
        orderable
        stock_level
      }
      sku
      upc
      variationValues {
        name
        value
      }
    }
    warrantyPlanId
    options {
      id
      name
      values {
        default
        id
        name
        price
      }
    }
  }
`;

export const ProductFragment = gql`
  fragment Product on Product {
    _meta {
      ...Meta
    }
    addOns {
      label
      addons {
        sku
        overlayImage {
          ...Asset
        }
      }
    }
    alternateColorsV2 {
      colors {
        color
        link {
          ...Link
        }
        sku
      }
    }
    alternateSizes {
      sizes {
        ...Link
      }
    }
    bestFor
    color
    customizable
    compatibleWith {
      link {
        ...Link
      }
      image {
        _meta {
          schema
        }
        altTag
        static
        image {
          _meta {
            ...Meta
          }
          accessibilityLabel
          defaultHost
          id
          name
          endpoint
          url(h: 24, w: 24)
        }
      }
    }
    dark
    contentRows {
      json
    }
    description
    exclusiveDealers {
      image {
        ...Asset
      }
      link {
        ...Link
      }
      title
    }
    featuresData {
      features {
        description
        featureType
        title
        link {
          link
        }
      }
    }
    frontFacingImages {
      closedImage {
        image {
          ...Asset
        }
      }
      openImage {
        image {
          ...Asset
        }
      }
    }
    foodCapacity {
      capacities {
        foodType
        title
        image {
          defaultHost
          endpoint
          id
          name
        }
      }
    }
    images {
      ...Asset
    }
    lifestyleImages {
      ...Asset
    }
    maintenance {
      youtubeLink {
        contentUrl
      }
    }
    ownerManual {
      ownerManualLink {
        _meta {
          ...Meta
        }
        displayName
        externalLink
      }
    }
    productData {
      newArrivalStart
      newArrivalEnd
      categories {
        _meta {
          ...Meta
        }
        categories
      }
      cookingSpace {
        tier {
          width
          depth
        }
      }
      pairings
    }
    promoMarkdown
    prop65 {
      _meta {
        ...Meta
      }
      title
      warningBody
      warningName
      warningType
    }
    ratingSummary {
      id
      average
      count
      distribution {
        Count
        RatingValue
      }
    }
    seo {
      ...Seo
    }
    shortDescription
    shortTitle
    shopData(currency: $currency) {
      ...Shop
    }
    smallHeading
    spinSetUrl
    template
    transparentImages {
      ...Asset
    }
    sizes {
      _meta {
        ...Meta
      }
      shortTitle
      shopData(currency: $currency) {
        ...Shop
      }
      frontFacingImages {
        closedImage {
          image {
            ...Asset
          }
        }
      }
      productData {
        cookingSpace {
          tier {
            depth
            width
          }
        }
      }
    }
    title
  }
`;
export const OptionItemFragment = gql`
  fragment OptionItem on LineItem {
    image
    title: name
    optionId
    optionValueId
    originalPrice
    price
    quantity
    sku
  }
`;

export const BaseLineItemFragment = gql`
  ${OptionItemFragment}
  fragment BaseLineItem on LineItem {
    baseDiscount
    basePrice
    bonus
    category
    description
    discount
    id
    image
    title: name
    originalPrice
    price
    productUrl
    quantity
    sku
    tax
    optionItems {
      ...OptionItem
    }
    gift
    giftMessage
    options {
      id
      name
      values {
        id
        price
        name
      }
    }
  }
`;

export const BasketLineItemFragment = gql`
  ${BaseLineItemFragment}
  fragment BasketLineItem on LineItem {
    ...BaseLineItem
    warrantyPlanId
    options {
      id
      name
      values {
        id
        price
        name
      }
    }
  }
`;

export const GiftCardFragment = gql`
  fragment GiftCard on GiftCard {
    id
    amount
    message
    email
    toName
    fromName
  }
`;

export const BasketFragment = gql`
  ${BasketLineItemFragment}

  fragment Basket on Basket {
    outOfStockItems {
      sku
    }
    id
    basketJWT
    checkoutUrl
    currency
    customerId
    discountTotal
    totalItems
    lineItems {
      ...BasketLineItem
    }
    freeProductTotal
    subtotal
    shippingTotal
    total
    tax
    shopToken
    vip
  }
`;

export const OrderFragment = gql`
  ${BaseLineItemFragment}
  ${GiftCardFragment}

  fragment Order on Order {
    details {
      basketId
      billingAddress {
        address1
        address2
        city
        fullName
        phone
        postalCode
        stateCode
        countryCode
      }
      couponItems {
        code
      }
      creationDate
      currency
      customerInfo {
        email
        customerNo
      }
      firstName
      giftCertificateItems {
        ...GiftCard
      }
      lastName
      orderNo
      orderPriceAdjustments {
        appliedDiscount {
          amount
          percentage
          type
        }
        couponCode
        itemText
        price
      }
      orderTotal
      packages {
        trackingUrl
        items {
          sku
          quantity
        }
        status
      }
      paymentInstruments {
        paymentCard {
          cardType
          expirationMonth
          expirationYear
          numberLastDigits
        }
      }
      productItems {
        ...BaseLineItem
      }
      shipments {
        shippingAddress {
          address1
          address2
          city
          countryCode
          fullName
          phone
          postalCode
          stateCode
        }
        shippingMethod {
          description
          name
        }
      }
      shippingTotal
      status
      subTotal
      taxTotal
      url
    }
  }
`;
